import { WidgetState, WidgetSkeleton, WidgetIssueState } from "@nexus/widget-sdk";
import { firstError, unwrap, Widget } from "@nexus/widget-sdk";
import type { WidgetManifest, WidgetModule, WidgetProps } from "@nexus/widget-sdk";
import { PROTECTION_OVERVIEW_EIS_CONTRACT } from "./types";
import type { ProtectionOverviewEisData } from "./types";

export interface ProtectionOverviewEisTrendConfig {
  title?: string;
}

export const manifest: WidgetManifest = {
  id: "protection-overview-eis-trend",
  name: "Protection Overview - EIS Trend",
  description: "One tenant's monthly Event Integrity Score trend for one report edition.",
  version: "1.0.0",
  dataRequests: {
    eis: {
      source: "protection-overview-tenant-eis",
      contract: PROTECTION_OVERVIEW_EIS_CONTRACT,
      configurable: true,
    },
  },
};

export const defaultConfig: ProtectionOverviewEisTrendConfig = {};

/** A minimal inline sparkline: widgets cannot import app/src, so this is not shared. */
function Sparkline({ values }: { values: number[] }) {
  if (values.length < 2) return null;
  const width = 220;
  const height = 56;
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const points = values
    .map((value, index) => {
      const x = (index / (values.length - 1)) * width;
      const y = height - ((value - min) / range) * height;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      height={height}
      className="text-brand-navy/60 dark:text-secondary"
      aria-hidden="true"
    >
      <polyline fill="none" stroke="currentColor" strokeWidth={2} points={points} />
    </svg>
  );
}

export function Component({ data, config }: WidgetProps<ProtectionOverviewEisTrendConfig>) {
  const error = firstError(data, ["eis"]);
  const result = unwrap<ProtectionOverviewEisData>(data.eis);
  const loading = data.eis?.isLoading && !result;
  const heading = config.title ?? (result ? `EIS trend - ${result.tenant.label}` : manifest.name);

  return (
    <Widget title={heading}>
      {error ? (
        <WidgetIssueState error={error} />
      ) : loading || !result ? (
        <WidgetSkeleton shape="chart" />
      ) : result.eisMonthly.length < 2 ? (
        <WidgetState title="No report data for this tenant in this edition." />
      ) : (
        <div className="flex flex-col gap-3 p-4">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-xs text-secondary">{result.tenant.region}</span>
            <span className="text-2xl font-bold tabular-nums text-brand-navy dark:text-white">
              {result.eisMonthly[result.eisMonthly.length - 1]}
            </span>
          </div>
          <Sparkline values={result.eisMonthly} />
        </div>
      )}
    </Widget>
  );
}

const widgetModule: WidgetModule<ProtectionOverviewEisTrendConfig> = {
  manifest,
  Component,
  defaultConfig,
};
export default widgetModule;
